<template>
  <div class="wrapper">
    <div class="content">
      <img
        src="../../assets/etoro-logo-big.svg"
        class="etoro-logo"
      >
      <h1 class="center">
        {{ currentTitle }}
      </h1>

      <div class="loader-container">
        <progress-bar-loader ref="progressLoader" />
      </div>
      <span class="text">{{ currentStep }}</span>
      <p
        class="link"
        @click="props.connectAccountsClicked"
      >
        Connect another bank account
      </p>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted } from 'vue';
import Segment from '../../Segment';
import ProgressBarLoader from '../Common/ProgressBarLoader.vue';

Segment.trackUserGot('EtoroOtsCashflowLoaderEntered');

const props = defineProps<{
  variant?: string,
  connectAccountsClicked:() => void;
}>();
// Config for each animation phase
const phaseConfig = [
  { title: 'Analysing your data', step: 'Step 1 of 3', animationTime: 20000, progressStart: 0, progressEnd: 100, shouldReset: true },
  { title: 'Building your forecast', step: 'Step 2 of 3', animationTime: 35000, progressStart: 0, progressEnd: 100, shouldReset: true },
  { title: 'Finalizing optimization', step: 'Step 3 of 3', animationTime: 20000, progressStart: 0, progressEnd: 70, shouldReset: true },
  { title: 'Finalizing optimization', step: 'Step 3 of 3', animationTime: 3000, progressStart: 70, progressEnd: 100, shouldReset: false },
];

// Refs for the current phase title and step
const currentTitle = ref(phaseConfig[0].title);
const currentStep = ref(phaseConfig[0].step);
const progressLoader = ref<InstanceType<typeof ProgressBarLoader> | null>(null);

// Function to start each phase based on the config
const startPhases = async () => {
  for (const [index, phase] of phaseConfig.entries()) {
    if (!progressLoader.value) { return; }

    if (phase.shouldReset) {
      await progressLoader.value.resetLoader();
    }
    currentTitle.value = phase.title;
    currentStep.value = phase.step;

    await new Promise<void>(resolve => {
      progressLoader.value!.animate(phase.animationTime, phase.progressEnd, phase.progressStart, () => {
        resolve();
      });
    });

    // Slight delay before the next phase starts
    if (index < phaseConfig.length - 1) {
      // eslint-disable-next-line no-promise-executor-return
      await new Promise(resolve => setTimeout(resolve, 300));
    }
  }
};

onMounted(() => {
  setTimeout(startPhases, 100);
});
</script>

<style lang="scss" scoped>
@import '../../scss/riseup-vars';

.wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  background-color: white;
  position: relative;
}

.content {
  display: flex;
  flex: 1;
  width: 100%;
  align-items: center;
  flex-direction: column;
}

.content h1 {
  color: #202125;
  font-size: 18px;
  font-family: CircularStdBook;
  font-weight: 700;
  line-height: 22px;
  word-wrap: break-word;
  margin-top: 56px;
  width: 80%;
  text-align: center;
}

.content .text {
  color: #205441;
  font-size: 16px;
  font-family: CircularStdBook, sans-serif;
  font-weight: 450;
  line-height: 22px;
  word-wrap: break-word;
}

.etoro-logo {
  margin-top: 60px;
  margin-bottom: 140px;
  width: 50%;
}

.link {
  position: absolute;
  bottom: 50px;
  color: #205441;
  font-size: 16px;
  font-family: CircularStdBook, sans-serif;
  font-weight: 450;
  line-height: 22px;
  word-wrap: break-word;
  cursor: pointer;
  text-decoration: underline;
  width: 100%;
  text-align: center;
}

.loader-container {
  width: 250px;
  margin: 10px 0;
}

</style>
